import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useGetInterviewSettings } from "@src/appV2/InterviewSettings/api/useGetInterviewSettings";
import {
  NotificationPreferencesDialog,
  type NotificationPreferencesDialogProps,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/NotificationPreferencesDialog";
import {
  type NotificationChangeArguments,
  type NotificationRow,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/types";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Dispatch, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RateNegotiationConfigEnum } from "src/components/RateNegotiationModal/interfaces";
import { BatchModal } from "src/containers/myAccount/BatchModal";
import { NotificationDetails } from "src/containers/myAccount/constants";
import { buildNotificationCellState } from "src/containers/teamMembers/buildNotificationCellState";
import { type TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { useRateNegotiationFlag } from "src/hooks/useRateNegotiationFlag";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";
import { SessionType } from "src/modules/interface";
import { type IUser } from "src/modules/myAccount/types";
import { configureAlertSettings, setFlags, updateNotification } from "src/modules/myAccount/utils";

interface ContainerProps {
  member?: IUser;
  visibility: boolean;
  dispatch: Dispatch<TeamMemberActions>;
}

function hasMobilePhone(member: IUser): boolean {
  return isDefined(member.phone) && member.phone.trim().length > 0;
}

export function NotificationPreferencesDialogContainer(props: ContainerProps) {
  const { member, visibility, dispatch: parentDispatch } = props;

  const reduxDispatch = useDispatch();
  const featureFlags = useFlags();
  const flags = useSelector((state: SessionType) => state.myAccount.flags);
  const alertSettings = useSelector((state: SessionType) => state.myAccount.alertSettings);
  const profile = useSelector((state: SessionType) => state.session.profile);
  const { wrapRestrictedAction } = useRestrictedSignedProfile();

  const { data: interviewSettings, isLoading: isLoadingInterviewSettings } =
    useGetInterviewSettings(
      { workplaceId: profile?.userId },
      { enabled: isDefined(profile) && visibility }
    );

  useEffect(() => {
    if (flags) {
      return;
    }
    reduxDispatch(setFlags(featureFlags));
  }, [reduxDispatch, featureFlags, flags]);

  useEffect(() => {
    if (!flags) {
      return;
    }
    configureAlertSettings(reduxDispatch, member);
  }, [reduxDispatch, flags, member]);

  const rateNegotiationDBStatus = profile?.rateNegotiation;
  const { featureFlagStatus: rateNegotiationFFStatus } = useRateNegotiationFlag({
    isAdmin: false,
    facilityRateNegotiationFlag: rateNegotiationDBStatus,
    facilityId: profile?.userId,
    facilityMsa: profile?.fullAddress?.metropolitanStatisticalArea ?? "",
    facilityState: profile?.fullAddress?.state ?? "",
  });

  const handleClose = useCallback(() => {
    parentDispatch({
      type: "SET_NOTIFICATIONS_MODAL_VISIBILITY",
      notificationModal: false,
    });
    parentDispatch({ type: "SET_MODAL_MEMBER", member: undefined });
  }, [parentDispatch]);

  const handleDone = useCallback(() => {
    parentDispatch({
      type: "SET_NOTIFICATIONS_MODAL_VISIBILITY",
      notificationModal: false,
    });
  }, [parentDispatch]);

  const handleChangeNotification = useCallback(
    (args: NotificationChangeArguments) => {
      void updateNotification(
        reduxDispatch,
        args.value,
        args.alertType.toUpperCase(),
        String(args.notificationKey),
        member
      );
    },
    [reduxDispatch, member]
  );

  const handleSelectorOpen = useMemo(
    () =>
      wrapRestrictedAction(() => undefined, {
        triggeredBy: "edit notifications",
        triggeredFrom: "Team Members",
      }),
    [wrapRestrictedAction]
  );

  const rows = useMemo<NotificationRow[]>(() => {
    if (!alertSettings || !member) {
      return [];
    }

    const allEmailDisabled = alertSettings.EMAIL.All?.default?.enabled === false;
    const allSmsDisabled = alertSettings.SMS.All?.default?.enabled === false;
    const memberHasNoPhone = !hasMobilePhone(member);
    const webPushAllNotifications = Boolean(member.notify?.WEB_PUSH?.All);

    const enabled = Object.values(NotificationDetails)
      .map((preference) => ({
        ...preference,
        description: preference.description.replace(" me ", " user "),
      }))
      .filter(({ flag }) => {
        if (!flag) {
          return true;
        }
        if (flag === CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION) {
          return (
            rateNegotiationFFStatus && rateNegotiationDBStatus === RateNegotiationConfigEnum.ON
          );
        }
        return flags?.[flag];
      })
      .filter(({ email }) => {
        if (
          email === "candidateApplyEmail" &&
          (isLoadingInterviewSettings || interviewSettings?.scheduleInterviews === true)
        ) {
          return false;
        }
        return true;
      });

    return enabled.map<NotificationRow>((notification, index) => {
      const emailFlagName = notification.emailFlag;
      const smsFlagName = notification.smsFlag;

      return {
        rowKey: `${String(notification.email)}-${String(notification.sms)}-${index}`,
        description: notification.description,
        cells: {
          email: buildNotificationCellState({
            alertType: "email",
            alertSetting: alertSettings.EMAIL[String(notification.email)]?.default,
            notification,
            notificationKey: notification.email,
            webPushAllNotifications,
            allDisabled: allEmailDisabled && String(notification.email) !== "All",
            flagDisabled: isDefined(emailFlagName) && !flags?.[emailFlagName],
            fieldDisabled: false,
          }),
          sms: buildNotificationCellState({
            alertType: "sms",
            alertSetting: alertSettings.SMS[String(notification.sms)]?.default,
            notification,
            notificationKey: notification.sms,
            webPushAllNotifications,
            allDisabled: memberHasNoPhone || (allSmsDisabled && String(notification.sms) !== "All"),
            flagDisabled: isDefined(smsFlagName) && !flags?.[smsFlagName],
            fieldDisabled: false,
          }),
          webPush: buildNotificationCellState({
            alertType: "web_push",
            alertSetting: undefined,
            notification,
            notificationKey: notification.web_push ?? false,
            webPushAllNotifications,
            allDisabled: false,
            flagDisabled: false,
            fieldDisabled: !notification.push,
          }),
        },
      };
    });
  }, [
    alertSettings,
    flags,
    interviewSettings?.scheduleInterviews,
    isLoadingInterviewSettings,
    member,
    rateNegotiationDBStatus,
    rateNegotiationFFStatus,
  ]);

  if (!member) {
    return null;
  }

  const dialogProps: NotificationPreferencesDialogProps = {
    isOpen: visibility,
    rows,
    smsDisabledNoPhone: !hasMobilePhone(member),
    onChangeNotification: handleChangeNotification,
    onSelectorOpen: handleSelectorOpen,
    onClose: handleClose,
    onDone: handleDone,
  };

  return (
    <>
      <NotificationPreferencesDialog {...dialogProps} />
      <BatchModal />
    </>
  );
}
